import React from "react";
import {
  Form,
  Input,
  DatePicker,
  Checkbox,
  Select,
  InputNumber,
  Badge,
  Tooltip,
  theme
} from "antd";
import { SketchPicker } from "react-color";
import { FaInfoCircle } from "react-icons/fa";
import dayjs from "dayjs";
import { RECUR_FORMAT, DEFAULT_SHIFT_COLOR, parseRecurDate } from "../../../../utils/shiftUtils";

const { RangePicker } = DatePicker;
export const WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

const idOf = value => value?._id || value;

// Employees list plus any assigned employee missing from it (e.g. when no list was loaded)
export const mergeEmployees = (employees = [], extra = []) => {
  const known = new Set(employees.map(e => e._id));
  return [...employees, ...extra.filter(e => e?._id && !known.has(e._id))];
};

export const EmployeeSelect = ({ employees = [], t, ...props }) => {
  const { token } = theme.useToken();
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={t("Select employees")}
      {...props}
    >
      {employees.map(emp => (
        <Select.Option key={emp._id} value={emp._id} label={`${emp.firstName} ${emp.lastName}`}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {emp.firstName} {emp.lastName}
            {emp.isOnLeave && (
              <Badge
                count={
                  <Tooltip title={`${t("On leave today")}: ${emp.leaveReason || ""}`}>
                    <FaInfoCircle style={{ color: token.colorWarning }} />
                  </Tooltip>
                }
              />
            )}
          </span>
        </Select.Option>
      ))}
    </Select>
  );
};

const ColorField = ({ value, onChange }) => (
  <SketchPicker color={value || DEFAULT_SHIFT_COLOR} onChangeComplete={c => onChange?.(c.hex)} />
);

export const ShiftFormFields = ({ employees, t }) => (
  <>
    <Form.Item
      name="title"
      label={t("Title")}
      rules={[{ required: true, message: t("Please enter title") }]}
    >
      <Input allowClear placeholder={t("enter", { field: t("Title") })} />
    </Form.Item>
    <Form.Item
      name="range"
      label={t("Timing")}
      extra={t("First and last day of the shift, with its daily start and end time")}
      rules={[{ required: true, message: t("Please enter range date") }]}
    >
      <RangePicker showTime={{ format: "HH:mm" }} format={RECUR_FORMAT} className="w-100" />
    </Form.Item>
    <Form.Item
      name="daysOfWeek"
      label={t("Recurring Days")}
      extra={t("Leave empty for a one-off shift")}
    >
      <Checkbox.Group options={WEEK_DAYS.map((day, i) => ({ label: t(day), value: i }))} />
    </Form.Item>
    <Form.Item name="employees" label={t("Employees")}>
      <EmployeeSelect employees={employees} t={t} />
    </Form.Item>
    <Form.Item
      name="minStaff"
      label={t("Minimum staff")}
      extra={t("The shift is marked understaffed when fewer employees are planned")}
    >
      <InputNumber min={0} max={100} />
    </Form.Item>
    <Form.Item name="color" label={t("Shifts Color")}>
      <ColorField />
    </Form.Item>
  </>
);

// Form values from a stored shift
export const shiftToFormValues = shift => {
  const toDayjs = (recur, fallback) => {
    const m = parseRecurDate(recur);
    if (m) return dayjs(m.toDate());
    return fallback ? dayjs(fallback) : null;
  };
  return {
    title: shift?.title || "",
    range: [toDayjs(shift?.startRecur, shift?.start), toDayjs(shift?.endRecur, shift?.end)],
    daysOfWeek: (shift?.daysOfWeek || []).map(Number),
    employees: (shift?.employees || []).map(idOf),
    minStaff: shift?.minStaff || 0,
    color: shift?.color || DEFAULT_SHIFT_COLOR
  };
};

// startRecur/endRecur keep the picked wall-clock time; start/end go out as ISO instants
export const formValuesToShift = values => {
  const [from, to] = values.range;
  return {
    title: values.title,
    startRecur: from.format(RECUR_FORMAT),
    endRecur: to.format(RECUR_FORMAT),
    start: from.toISOString(),
    end: to.toISOString(),
    daysOfWeek: values.daysOfWeek || [],
    employees: values.employees || [],
    minStaff: values.minStaff || 0,
    color: values.color || DEFAULT_SHIFT_COLOR
  };
};

export const formValuesToTemplate = values => {
  const [from, to] = values.range;
  return {
    title: values.title,
    startTime: from.format("HH:mm"),
    endTime: to.format("HH:mm"),
    daysOfWeek: values.daysOfWeek || [],
    employees: values.employees || [],
    minStaff: values.minStaff || 0,
    color: values.color || DEFAULT_SHIFT_COLOR
  };
};

// Human-readable problem with the values, or null
export const validateShiftValues = (values, t) => {
  const [from, to] = values.range || [];
  if (!from || !to || !to.isAfter(from)) return t("The end must be after the start");
  if (!(values.daysOfWeek || []).length && to.diff(from, "hour", true) > 24) {
    return t("Select the recurring days, or pick a range under 24 hours for a one-off shift");
  }
  return null;
};
